import React, { ReactElement, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  gray80,
  gray60,
  gray40,
  white,
  white_overlay80,
  primaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldXxl, sectionTitle, regularMd, spHeaderTitle } from '../../../constants/location';
import {
  GROUP_MANAGEMENT_TITLE,
  GROUP_CREATE_TITLE,
  GROUP_EMPTY_MESSAGE,
} from '../../../constants/labels';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { useCompanyId } from '../../../hooks/useCompanyId';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { isNotCompanyMember } from '../../../utils/permission';
import SideBar from '../../uiParts/menu/SideBar';
import MetaTitle from '../../utils/MetaTitle';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import { getGroups } from '../../../api/firebase/firestore/group/read';
import { deleteGroup } from '../../../api/firebase/firestore/group/delete';
import { getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import { getAllPatternsByCompanyId } from '../../../api/firebase/firestore/pattern/read';
import { shareScope } from '../../../constants/enum';
import type { PatternType } from '../../../types/pattern';
import GroupListCard from '../../uiParts/card/GroupListCard';
import type { GroupType } from '../../../types/group';
import type { UserType } from '../../../types/user';

const styles = {
  headerTitle: {
    ...boldXxl,
    '@media (max-width: 960px)': { ...spHeaderTitle },
  },
  createButton: {
    border: 'none',
    fontWeight: 700,
    fontSize: '16px',
    width: '192px',
    height: '44px',
  },
  // SPではヘッダーのボタンを隠し、下部固定のボタンを表示する（担当者一覧と同じ作り）
  notDisplayMobile: {
    '@media (max-width: 960px)': { display: 'none' },
  },
  mobileCreateButton: {
    display: 'none',
    '@media screen and (max-width:960px)': {
      display: 'block',
      boxSizing: 'border-box',
      position: 'fixed',
      bottom: 0,
      left: 0,
      backgroundColor: white_overlay80,
      padding: '16px 16px 24px',
      width: '100%',
      zIndex: 1,
    },
  },
  mobileCreateButtonInner: {
    border: 'none',
    fontWeight: 700,
    fontSize: '16px',
    height: '44px',
    width: '100%',
  },
  mainContainer: {
    width: 'calc(100% - 64px)',
    margin: '0 32px 16px 32px',
    boxSizing: 'border-box',
    '@media screen and (max-width:960px)': {
      margin: '0 16px',
      width: 'calc(100% - 32px)',
    },
  },
  // 空状態も一覧時と同じ白カード内に表示する（読取パターン一覧の tableSection と同じ作り。#2925）
  emptySection: {
    backgroundColor: white,
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    boxSizing: 'border-box',
    minHeight: '560px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    '@media screen and (max-width:960px)': {
      border: 'none',
      borderRadius: 0,
      minHeight: '400px',
    },
  },
  // 空状態は APIキー管理・受信トレイ画面と同じ アイコン + タイトル + 説明 の3点構成に統一（#2925）
  emptyWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    padding: '64px 16px',
    textAlign: 'center' as const,
  },
  emptyIcon: {
    color: gray60,
    height: '56px',
    marginBottom: '24px',
    width: '56px',
  },
  emptyTitle: {
    ...sectionTitle,
    marginBottom: '8px',
  },
  emptyDescription: {
    ...regularMd,
    color: gray80,
  },
};

/**
 * グループ管理画面（#2804）
 * 企業管理者・運営のみアクセス可能。一般メンバーはリダイレクト。
 */
const GroupList: React.FC = () => {
  const { user, isLoading } = useContext(AuthContext);
  const { companyId: effectiveCompanyId } = useCompanyId();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();

  const [groups, setGroups] = useState<GroupType[]>([]);
  const [patterns, setPatterns] = useState<PatternType[]>([]);
  const [memberNameMap, setMemberNameMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteTarget, setDeleteTarget] = useState<GroupType | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);

  const companyId = effectiveCompanyId || user?.company_id;

  const showMessage = (message: string, error = false): void => {
    setSnackbarMessage(message);
    setIsError(error);
    setSnackbarOpen(true);
  };

  const fetchGroups = useCallback(async (): Promise<void> => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [fetchedGroups, users, fetchedPatterns] = await Promise.all([
        getGroups(companyId),
        getUsersByCompanyId(companyId),
        getAllPatternsByCompanyId(companyId),
      ]);
      setGroups(fetchedGroups);
      setPatterns(fetchedPatterns);
      setMemberNameMap(
        (users as UserType[]).reduce<Record<string, string>>((acc, u) => {
          acc[u.uid] = u.name || '(名前未設定)';
          return acc;
        }, {}),
      );
    } catch (error) {
      Sentry.captureException(error);
      showMessage('グループの取得に失敗しました', true);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // 未ログイン・権限チェック
  useEffect(() => {
    if (isLoading) return;
    if (user === null) {
      return;
    }
    if (!isNotCompanyMember(user.role)) {
      navigate('/pattern');
    }
  }, [user, isLoading, navigate]);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups]);

  const handleDelete = async (): Promise<void> => {
    if (!companyId || !user || !deleteTarget) return;
    setDeleting(true);
    try {
      await deleteGroup(companyId, deleteTarget.id, user.uid);
      setDeleteTarget(null);
      showMessage('グループを削除しました');
      await fetchGroups();
    } catch (error) {
      Sentry.captureException(error);
      showMessage('グループの削除に失敗しました', true);
    } finally {
      setDeleting(false);
    }
  };

  const renderContent = (): ReactElement => {
    if (loading) {
      return <CustomLoading loading={true} />;
    }
    if (groups.length === 0) {
      return (
        <Box sx={styles.mainContainer}>
          <Box sx={styles.emptySection}>
            <Box sx={styles.emptyWrapper}>
              <GroupsIcon sx={styles.emptyIcon} />
              <Typography sx={styles.emptyTitle}>{GROUP_EMPTY_MESSAGE}</Typography>
              <Typography sx={styles.emptyDescription}>
                右上の「{GROUP_CREATE_TITLE}」ボタンから作成してください。
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={styles.mainContainer}>
        <GroupListCard
          groups={groups}
          memberNameMap={memberNameMap}
          onEdit={(groupId): void =>
            navigate('/group/edit', { state: { groupId, from: '/group' } })
          }
          onDelete={(group): void => setDeleteTarget(group)}
        />
      </Box>
    );
  };

  // 削除対象グループにのみ共有されている読取パターン（削除後に孤立し閲覧できなくなる
  // もの。#2886で企業管理者も所属グループのみ閲覧可となったため）を抽出して、
  // 削除ダイアログで注意喚起する（#2804）
  const deleteDescription = useMemo<string>(() => {
    const baseDeleteDescription = 'このグループを削除します。よろしいですか？';
    if (!deleteTarget) return baseDeleteDescription;
    const orphanedPatterns = patterns.filter(
      p =>
        p.share_scope === shareScope.group &&
        (p.shared_group_ids ?? []).length === 1 &&
        (p.shared_group_ids ?? []).includes(deleteTarget.id),
    );
    if (orphanedPatterns.length === 0) return baseDeleteDescription;
    const names = orphanedPatterns.map(p => `「${p.pattern_name}」`);
    const preview = names.slice(0, 3).join('、') + (names.length > 3 ? ' 他' : '');
    return `このグループにのみ共有されている読取パターンが${orphanedPatterns.length}件あります（${preview}）。削除すると、これらのパターンは閲覧できなくなります。${baseDeleteDescription}`;
  }, [deleteTarget, patterns]);

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={layoutStyles.header}>
          <Typography sx={styles.headerTitle}>{GROUP_MANAGEMENT_TITLE}</Typography>
          <Button
            variant="outlined"
            sx={[primaryButtonBackgroundColor, styles.createButton, styles.notDisplayMobile]}
            onClick={(): void => navigate('/group/register')}
          >
            ＋ {GROUP_CREATE_TITLE}
          </Button>
        </Box>
        {renderContent()}
      </Box>

      {/* SP用: 画面下部に固定の「グループを作成」ボタン（担当者一覧の招待ボタンと同じ作り） */}
      <Box sx={styles.mobileCreateButton}>
        <Button
          variant="outlined"
          fullWidth
          sx={[primaryButtonBackgroundColor, styles.mobileCreateButtonInner]}
          onClick={(): void => navigate('/group/register')}
        >
          ＋ {GROUP_CREATE_TITLE}
        </Button>
      </Box>

      <DeleteModal
        open={deleteTarget !== null}
        onClose={(): void => setDeleteTarget(null)}
        onDelete={(): void => void handleDelete()}
        title={deleteTarget?.name ?? ''}
        loading={deleting}
        description={deleteDescription}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={isError ? snackbarMessage : ''}
        infoText={!isError ? snackbarMessage : ''}
      />
    </Box>
  );
};

export default GroupList;
